import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../stores/useAuthStore';
import { Link, useNavigate } from 'react-router-dom';
import { VersiculoDelDia } from '../emprendedores-de-fe/VersiculoDelDia';

interface LoginFormData {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const {
    login,
    isLoading,
    error,
    clearError,
    authResendHintEmail,
    resendSignupEmail,
    showCompanySelector: _showCompanySelector,
    isAuthenticated: _isAuthenticated,
  } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Access store methods for direct state checking
  const authStore = useAuthStore;
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    mode: 'onChange'
  });

  const onSubmit = async (data: LoginFormData) => {
    // SECURITY: Never log passwords or credentials
    clearError();
    try {
      await login(data.email, data.password);

      // Wait long enough for any concurrent checkSession to also finish
      await new Promise(resolve => setTimeout(resolve, 300));

      // Check if authentication was actually successful
      const authState = authStore.getState();
      
      // CRITICAL: Check for error first, then authentication status
      if (authState.error) {
        console.error('❌ Login failed - error in state:', authState.error);
        // Error is already displayed in the UI, don't proceed
        return;
      }
      
      if (!authState.isAuthenticated) {
        console.error('❌ Login failed - not authenticated');
        // Don't proceed if not authenticated
        return;
      }
      
      console.log('✅ Login successful');
      console.log('🔍 showCompanySelector after login:', authState.showCompanySelector);
      
      // Check if user needs to select a company
      if (authState.showCompanySelector) {
        console.log('User has multiple companies, showing selector');
        // Don't redirect - let the CompanySelector component handle the selection
        return;
      }
      
      console.log('Redirecting to dashboard');
      // Use React Router navigation instead of window.location
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Login error caught in try-catch:', error);
      // Error is already set in the store by the login function
      // Don't proceed with navigation if there's an error
      // Make sure we check state one more time
      const authState = authStore.getState();
      if (authState.error || !authState.isAuthenticated) {
        console.error('❌ Confirmed login failure - not proceeding');
        return;
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-2xl text-white font-bold">C</span>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Bienvenido a Clientelo
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Tu CRM con Business Intelligence para LATAM
          </p>
        </div>

        <VersiculoDelDia variant="login" />

        {/* Formulario de Login */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Campo Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Correo Electrónico
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register('email', {
                    required: 'El email es requerido',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'El formato del email no es válido'
                    }
                  })}
                  className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="tu@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="form-label">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password', {
                    required: 'La contraseña es requerida',
                    minLength: {
                      value: 6,
                      message: 'La contraseña debe tener al menos 6 caracteres'
                    }
                  })}
                  className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Error del servidor */}
                      {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1 space-y-2">
                    <p className="text-sm text-red-800">{error}</p>
                    {authResendHintEmail && (
                      <div className="pt-1">
                        <button
                          type="button"
                          disabled={resendBusy}
                          onClick={async () => {
                            setResendMessage(null);
                            setResendBusy(true);
                            try {
                              await resendSignupEmail(authResendHintEmail);
                              setResendMessage(
                                'Si el correo está configurado en el servidor, recibirás un nuevo enlace en unos minutos. Revisa spam.'
                              );
                            } catch (e) {
                              setResendMessage(
                                e instanceof Error ? e.message : 'No se pudo reenviar. Intenta más tarde.'
                              );
                            } finally {
                              setResendBusy(false);
                            }
                          }}
                          className="text-sm font-semibold text-red-900 underline disabled:opacity-50"
                        >
                          {resendBusy ? 'Enviando…' : 'Reenviar enlace de verificación'}
                        </button>
                        {resendMessage && (
                          <p className="text-xs text-red-700 mt-1">{resendMessage}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* Opciones adicionales */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Recordarme
              </label>
            </div>

            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-blue-600 hover:text-blue-500">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </div>

          {/* Botón de Login */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Iniciando sesión...
                </div>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </div>

          {/* Enlaces adicionales */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              ¿No tienes una cuenta?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                Regístrate aquí
              </Link>
            </p>
          </div>
        </form>

        <div className="mt-8 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-left">
            <p className="text-sm text-blue-900">
              Usa el <strong>correo y la contraseña</strong> con los que te registraste. Si no recuerdas la contraseña, usa{' '}
              <Link to="/forgot-password" className="font-semibold underline">
                ¿Olvidaste tu contraseña?
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
